// ─── Envelope parsing ────────────────────────────────────────────────────
/**
 * Safely extract the guard-decline envelope from a failed mutation's parsed
 * error body. Guard-blocked 4xx responses additively include
 * `{declines: [...], can_override: bool}` alongside the legacy error fields.
 *
 * Returns null when the envelope is absent or malformed — callers must then
 * fall back to their existing error handling (toast/alert).
 */
export function parseGuardDeclines(errorBody) {
    if (!errorBody || typeof errorBody !== 'object' || Array.isArray(errorBody))
        return null;
    const body = errorBody;
    if (!Array.isArray(body.declines))
        return null;
    const declines = [];
    for (const entry of body.declines) {
        if (!entry || typeof entry !== 'object')
            continue;
        const e = entry;
        if (typeof e.message !== 'string' || !e.message)
            continue;
        declines.push({
            check: typeof e.check === 'string' ? e.check : '',
            code: typeof e.code === 'string' ? e.code : '',
            message: e.message,
            overridable: e.overridable === true,
            metadata: e.metadata && typeof e.metadata === 'object' ? e.metadata : undefined,
        });
    }
    if (declines.length === 0)
        return null;
    return { declines, canOverride: body.can_override === true };
}
/**
 * Preflight-evaluate the configured guard rules for a mutation that hasn't
 * happened yet (e.g. while building a new work order). Requires
 * `workorders:create`. Always resolves 200 with the effective action,
 * blocks (`declines`), advisories (`warnings`) and overridability.
 *
 * Throws on transport/HTTP failure — callers doing advisory preflight must
 * catch and fail silent (the server still enforces on the real mutation).
 */
export async function evaluateGuard(client, locationId, req) {
    const response = await client.post(`/locations/${locationId}/guard/evaluate`, req);
    if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to evaluate work order rules');
    }
    return response.data;
}
